import { api } from './api';
import type { Pagination } from './group.service';
import type { UserRef, TagRef, DependencyRef, DependentRef } from './goal.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface Task {
  id: string;
  reference_id: string;
  title: string;
  description: string | null;
  task_type: string;
  phase_status: string;
  calculated_status: string;
  priority: string;
  start_date: string | null;
  end_date: string | null;
  completed_at: string | null;
  assignee_id: string | null;
  creator_id: string;
  group_id: string | null;
  goal_id: string | null;
  sprint_id: string | null;
  column_id: string | null;
  team_id: string | null;
  display_order: number;
  estimated_hours: number | null;
  actual_hours: number;
  timer_started_at: string | null;
  accumulated_seconds: number;
  evaluation_score: number | null;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
  assignee?: UserRef | null;
  creator?: UserRef | null;
}

export interface EstimationHistoryEntry {
  id: string;
  old_value: number | null;
  new_value: number;
  reason: string | null;
  changed_by: { id: string; full_name: string };
  created_at: string;
}

export interface TaskDetail extends Task {
  goal?: { id: string; title: string; reference_id: string } | null;
  sprint?: { id: string; name: string; reference_id: string } | null;
  column?: { id: string; name: string; display_order: number } | null;
  team?: { id: string; name: string; reference_id: string } | null;
  group?: { id: string; name: string; reference_id: string } | null;
  evaluators: { id: string; user: UserRef }[];
  tags: TagRef[];
  sprint_goals: { id: string; title: string }[];
  dependencies: DependencyRef[];
  dependents: DependentRef[];
  estimation_history: EstimationHistoryEntry[];
  first_timer_start: string | null;
}

export interface Attachment {
  id: string;
  entity_type: string;
  entity_id: string;
  attachment_type: string;
  file_name: string | null;
  file_size: number | null;
  file_type: string | null;
  url: string | null;
  uploaded_by: string;
  created_at: string;
}

export interface TimerStatusData {
  running: boolean;
  timer_started_at: string | null;
  accumulated_seconds: number;
  actual_hours: number;
  current_elapsed: number;
}

export interface TimerLogEntry {
  id: string;
  action: string;
  accumulated_seconds: number;
  created_at: string;
  user?: { id: string; full_name: string } | null;
}

export interface TaskListParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  task_type?: string;
  phase_status?: string;
  calculated_status?: string;
  priority?: string;
  assignee_id?: string;
  group_id?: string;
  goal_id?: string;
  sprint_id?: string;
  team_id?: string;
  tag_id?: string;
  show_completed?: boolean;
}

export interface CreateTaskData {
  title: string;
  description?: string;
  task_type: string;
  priority?: string;
  start_date: string;
  end_date: string;
  assignee_id: string;
  estimated_hours?: number;
  goal_id?: string;
  sprint_id?: string;
  column_id?: string;
  team_id?: string;
  group_id?: string;
  sprint_goal_ids?: string[];
  tags?: string[];
}

export const TASK_TYPES = ['goal', 'sprint', 'general'] as const;
export const PHASE_STATUSES = ['not_started', 'in_progress', 'done'] as const;
export const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const taskService = {
  list: (params?: TaskListParams) =>
    api.get<{ success: boolean; data: Task[]; pagination: Pagination }>('/tasks', { params }),

  listMy: (params?: TaskListParams) =>
    api.get<{ success: boolean; data: Task[]; pagination: Pagination }>('/tasks/my', { params }),

  get: (id: string) =>
    api.get<{ success: boolean; data: TaskDetail }>(`/tasks/${id}`),

  create: (data: CreateTaskData) =>
    api.post<{ success: boolean; data: Task }>('/tasks', data),

  update: (id: string, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: TaskDetail }>(`/tasks/${id}`, data),

  updateStatus: (id: string, phase_status: string) =>
    api.patch(`/tasks/${id}/status`, { phase_status }),

  move: (id: string, column_id: string, display_order: number) =>
    api.patch(`/tasks/${id}/move`, { column_id, display_order }),

  delete: (id: string) =>
    api.delete(`/tasks/${id}`),

  // Dependencies
  addDependency: (id: string, depends_on_id: string) =>
    api.post(`/tasks/${id}/dependencies`, { depends_on_id }),
  removeDependency: (id: string, depId: string) =>
    api.delete(`/tasks/${id}/dependencies/${depId}`),

  // Timer
  timerStart: (id: string) => api.post<{ success: boolean; data: TimerStatusData }>(`/tasks/${id}/timer/start`),
  timerPause: (id: string) => api.post<{ success: boolean; data: TimerStatusData }>(`/tasks/${id}/timer/pause`),
  timerStop: (id: string) => api.post<{ success: boolean; data: TimerStatusData }>(`/tasks/${id}/timer/stop`),
  timerStatus: (id: string) => api.get<{ success: boolean; data: TimerStatusData }>(`/tasks/${id}/timer/status`),
  timerLogs: (id: string, params?: { cursor?: string; limit?: number }) =>
    api.get<{ success: boolean; data: TimerLogEntry[]; pagination: Pagination }>(`/tasks/${id}/timer/logs`, { params }),
  updateActualHours: (id: string, actual_hours: number) =>
    api.patch(`/tasks/${id}/actual-hours`, { actual_hours }),

  // Attachments
  listAttachments: (id: string) =>
    api.get<{ success: boolean; data: Attachment[] }>(`/attachments/task/${id}`),
  uploadAttachment: (entityType: string, entityId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    return api.post<{ success: boolean; data: Attachment }>('/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addUrlAttachment: (entityType: string, entityId: string, url: string, file_name?: string) =>
    api.post<{ success: boolean; data: Attachment }>('/attachments/url', { entity_type: entityType, entity_id: entityId, url, file_name }),
  downloadAttachment: (attId: string) =>
    api.get<{ success: boolean; data: { url: string; type: string } }>(`/attachments/${attId}/download`),
  deleteAttachment: (attId: string) =>
    api.delete(`/attachments/${attId}`),
};
